import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useHistory, useLocation, Link } from 'react-router-dom'
import { Flex, SecondaryHeader } from '../styles/global'
import { Paper, Button } from '@material-ui/core'
import { Input } from '../components/Input'

import { confirmResetPassword } from '@aws-amplify/auth'

interface UserCredentials {
  verification_code: string;
  password1: string;
  password2: string;
}

export const ResetPassword = () => {
  const [formData, setFormData] = useState<UserCredentials>({ verification_code: '', password1: '', password2: '' })
  const [formTouched, setFormTouched] = useState<{ verification_code: boolean, password1: boolean, password2: boolean }>({ verification_code: false, password1: false, password2: false })

  const [isLoading, setLoading] = useState(false)
  const history = useHistory()
  const location = useLocation()

  // Extract and validate username from URL params
  const urlParams = new URLSearchParams(location.search)
  const username = urlParams.get('username')

  useEffect(() => {
    // Redirect to forgot-password if no username is provided
    if (!username) {
      alert('No email address provided. Please start the password reset process again.')
      history.push('/forgot-password')
    }
  }, [username, history])

  const isValid = formData && formData.verification_code !== '' && formData.password1 !== '' && formData.password2 !== '' && formData.password1 === formData.password2

  const sendCredentials = () => {
    if (!username) {
      alert('No email address provided. Please start the password reset process again.')
      history.push('/forgot-password')
      return
    }

    setLoading(true)

    const { password1, verification_code } = formData

    confirmResetPassword({
      username: username.trim(),
      confirmationCode: verification_code.trim(),
      newPassword: password1.trim()
    })
      .then(async () => {
        alert('Password reset successfully! Please log in with your new password.')
        history.push('/login')
        setLoading(false)
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
        alert(err.message)
      })
  }

  return (
    <Container>
      <LoginBox elevation={1}>
        <Flex direction={'column'}>
          <SecondaryHeader>Reset Your Password</SecondaryHeader>
          <StyledInput
            id="verification-code"
            label="Verification Code"
            setWidth="100%"
            required={formTouched.verification_code}
            requiredString="Verification Code required."
            setValue={(value: string) => {
              setFormTouched({ ...formTouched, verification_code: true })
              setFormData(() => {
                return { ...formData, verification_code: value };
              });
            }}
            value={formData.verification_code}
          />
          <StyledInput
            id="new-password"
            label="New Password"
            setWidth="100%"
            type="password"
            required={formTouched.password1}
            requiredString="Password required."
            setValue={(value: string) => {
              setFormTouched({ ...formTouched, password1: true })
              setFormData(() => {
                return { ...formData, password1: value };
              });
            }}
            value={formData.password1}
          />
          <StyledInput
            id="confirm-new-password"
            label="Confirm New Password"
            setWidth="100%"
            type="password"
            required={formTouched.password2}
            requiredString="Password required."
            setValue={(value: string) => {
              setFormTouched({ ...formTouched, password2: true })
              setFormData(() => {
                return { ...formData, password2: value };
              });
            }}
            value={formData.password2}
          />
          <Button disabled={!isValid || isLoading} onClick={() => sendCredentials()} variant="contained" color="primary">
            Reset Password
          </Button>
          <StyledLink to='/login'>
            Back to Login
          </StyledLink>
        </Flex>
      </LoginBox>
    </Container>
  )
}

const StyledInput = styled(Input)`
  margin-bottom: 24px !important;
  
  .MuiOutlinedInput-root {
    border-radius: 10px;
    background: #f7fafc;
    transition: all 0.2s ease;
    
    &:hover {
      background: #edf2f7;
    }
    
    &.Mui-focused {
      background: white;
      box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
    }
  }
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #1a202c 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(63, 81, 181, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(63, 81, 181, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`

const LoginBox = styled(Paper)`
  padding: 48px;
  display: flex;
  justify-content: center;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  background: white;
  min-width: 400px;
  position: relative;
  z-index: 1;
  
  ${Flex} {
    width: 100%;
    
    ${StyledInput} {
      margin-bottom: 20px;
    }
    
    ${SecondaryHeader} {
      margin-bottom: 36px;
      text-align: center;
      font-size: 28px;
      font-weight: 800;
      color: #1a202c;
      letter-spacing: -0.5px;
    }
  }
  
  .MuiButton-containedPrimary {
    background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
    border-radius: 10px;
    padding: 14px 24px;
    font-weight: 700;
    text-transform: none;
    font-size: 16px;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 14px rgba(63, 81, 181, 0.4);
    transition: all 0.2s ease;
    
    &:hover {
      background: linear-gradient(135deg, #303f9f 0%, #3f51b5 100%);
      box-shadow: 0 6px 20px rgba(63, 81, 181, 0.5);
      transform: translateY(-1px);
    }
    
    &:disabled {
      background: #e2e8f0;
      box-shadow: none;
      color: #a0aec0;
    }
  }
`

const StyledLink = styled(Link)`
  text-align: center;
  margin-top: 24px !important;
  text-decoration: none;
  color: #3f51b5;
  font-weight: 600;
  font-size: 14px;
  transition: color 0.2s ease;
  
  &:hover {
    color: #303f9f;
    text-decoration: underline;
  }
`
